import { useEffect, useMemo, useState } from 'react';

import {
	Autocomplete,
	Chip,
	Grid,
	InputLabel,
	MenuItem,
	Stack,
	TextField,
	useTheme,
} from '@mui/material';
import MainCard from 'components/cards/MainCard';
import SkeltonTotalGrowthBarChart from 'components/cards/Skeleton/TotalGrowthBarChart';
import EmptyData from 'components/EmptyData/EmptyData';
import PageTitle from 'components/Title/PageTitle';
import {
	Region,
	regionEnumOptions,
} from 'modules/RegionsConfiguration/constants/enums';
import getEnumOptionLabel from 'modules/shared/utils/getEnumOptionLabel';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { IChartProps } from '../interfaces/Charts';
import {
	IEndUsersCountStatsByRegion,
	IEndUsersCountStatsByRegionLesson,
} from '../interfaces/Stats';
import {
	DashboardStateSelector,
	getRegionLessonsUsersCounts,
	getRegionsUsersCounts,
} from '../store/slice';

const RegionsEndUsersCount = () => {
	const dispatch = useDispatch();
	const { regionsUsersCount, isLoading } = useSelector(DashboardStateSelector);
	const [region, setRegion] = useState<number>(-1);

	const [selectedLessonsIds, setSelectedLessonsIds] = useState<number[]>([]);

	const theme: any = useTheme();
	const primaryMain = theme.palette.primary.main;
	const warningDark = theme.palette.warning.dark;

	const handleRegionChange = (region: number) => {
		setSelectedLessonsIds([]);
		setRegion(region);
		if (region === -1) dispatch(getRegionsUsersCounts({}));
		else dispatch(getRegionLessonsUsersCounts({ region }));
	};

	const handleLessonsChange = (_: any, values: number[]) => {
		setSelectedLessonsIds(values);
	};

	const handleSelectLesson = (
		lesson: IEndUsersCountStatsByRegionLesson['lesson'],
	) => {
		window.open(
			`/#/list-of-regions/${region}/${lesson.type}/update/${lesson.id}`,
		);
	};

	const getLessonLabel = (lessonId: number) => {
		return (
			regionsUsersCount.regionLessonsData.find(
				item => item.lesson.id === lessonId,
			)?.lesson.name || ''
		);
	};

	useEffect(
		() => {
			if (isLoading) setRegion(-1);
		},
		// eslint-disable-next-line
		[isLoading],
	);

	const showLessonsChart = region !== -1;
	const showLessonsFilter = regionsUsersCount.regionLessonsData.length > 1;
	const selectedLessonsIsEmpty = selectedLessonsIds.length === 0;

	const regionHasLessons = regionsUsersCount.regionLessonsData.length > 0;

	const filteredRegionLessonsData = useMemo<
		IEndUsersCountStatsByRegionLesson[]
	>(() => {
		if (selectedLessonsIsEmpty) {
			return regionsUsersCount.regionLessonsData;
		}

		return regionsUsersCount.regionLessonsData.filter(item =>
			selectedLessonsIds.includes(item.lesson.id),
		);
	}, [
		selectedLessonsIds,
		selectedLessonsIsEmpty,
		regionsUsersCount.regionLessonsData,
	]);

	const showEmptyStatus =
		!regionsUsersCount.isLoading &&
		(regionsUsersCount.data.length === 0 ||
			(showLessonsChart && regionsUsersCount.regionLessonsData.length === 0));

	const chartRender = regionsUsersCount.isLoading ? (
		<SkeltonTotalGrowthBarChart />
	) : (
		<Chart
			{...(showLessonsChart
				? getRegionLessonsChartProps(
						filteredRegionLessonsData,
						warningDark,
						handleSelectLesson,
				  )
				: getRegionsChartProps(
						regionsUsersCount.data,
						primaryMain,
						handleRegionChange,
				  ))}
		/>
	);

	return (
		<MainCard
			noPageTitle
			title={<PageTitle>Number of users by region</PageTitle>}
		>
			<Stack justifyContent={'center'} alignItems='center'>
				<Grid
					container
					width='1000px'
					maxWidth='100%'
					alignItems='start'
					paddingBottom={2}
				>
					<Grid item xs={12} padding={1}>
						<Stack spacing={1}>
							<InputLabel htmlFor='type'>Region</InputLabel>
							<TextField
								fullWidth
								name={'type'}
								id={'type'}
								placeholder='Type'
								size='medium'
								select
								value={region}
								onChange={e => {
									const value = Number.parseInt(e.target.value);
									handleRegionChange(value);
								}}
							>
								<MenuItem value={-1}>All</MenuItem>
								{regionEnumOptions.map(type => (
									<MenuItem key={type.value} value={type.value}>
										{type.label}
									</MenuItem>
								))}
							</TextField>
						</Stack>
					</Grid>
					{showLessonsChart && regionHasLessons && showLessonsFilter && (
						<Grid item xs={12} padding={1}>
							<Stack spacing={1}>
								<InputLabel htmlFor='lessons'>Lessons to compare</InputLabel>
								<Autocomplete
									fullWidth
									multiple
									id='lessons'
									value={selectedLessonsIds}
									onChange={handleLessonsChange}
									options={regionsUsersCount.regionLessonsData.map(
										item => item.lesson.id,
									)}
									getOptionLabel={option => getLessonLabel(option)}
									renderTags={(tagValue, getTagProps) =>
										tagValue.map((option, index) => (
											<Chip
												label={getLessonLabel(option)}
												{...getTagProps({ index })}
											/>
										))
									}
									renderInput={params => (
										<TextField
											{...params}
											fullWidth
											placeholder='Search lessons by name'
										/>
									)}
								/>
							</Stack>
						</Grid>
					)}
				</Grid>
			</Stack>
			{showEmptyStatus ? (
				<EmptyData message='No data found, try to change the region.' />
			) : (
				chartRender
			)}
		</MainCard>
	);
};

export default RegionsEndUsersCount;

const getRegionsChartProps = (
	stats: IEndUsersCountStatsByRegion[],
	barBackground: string,
	onSelectRegion: (region: number) => void,
): IChartProps => {
	const formattedStats: IEndUsersCountStatsByRegion[] = regionEnumOptions.map(
		({ value }) => ({
			region: value as Region,
			totalCount: stats.find(stat => stat.region === value)?.totalCount || 0,
		}),
	);

	const xaxis: ApexXAxis = {
		type: 'category',
		categories: formattedStats.map(stat =>
			getEnumOptionLabel(regionEnumOptions, stat.region),
		),
		title: {
			text: 'Regions',
			style: {
				fontSize: '18px',
			},
		},
	};
	const yaxis: number[] = formattedStats.map(stat => stat.totalCount);

	return {
		height: 600,
		type: 'bar',
		options: {
			colors: [barBackground],
			title: {
				text: '',
				align: 'center',
				style: {
					fontSize: '24px',
				},
			},
			chart: {
				id: 'bar-chart',
				stacked: true,
				toolbar: {
					show: true,
				},
				animations: {
					enabled: true,
					easing: 'easeinout',
					speed: 800,
					animateGradually: {
						enabled: true,
						delay: 150,
					},
					dynamicAnimation: {
						enabled: true,
						speed: 350,
					},
				},
				events: {
					dataPointSelection: function (_, __, config) {
						onSelectRegion(config.dataPointIndex);
					},
				},
			},
			responsive: [
				{
					breakpoint: 480,
					options: {
						legend: {
							position: 'bottom',
							offsetX: -10,
							offsetY: 0,
						},
					},
				},
			],
			plotOptions: {
				bar: {
					borderRadius: 4,
					horizontal: false,
				},
			},
			xaxis: xaxis,
			yaxis: {
				title: {
					text: 'Number of users',

					style: {
						fontSize: '18px',
					},
				},
				tickAmount: 1,
				labels: {
					formatter: value => {
						return value.toString();
					},
				},
			},
			legend: {
				show: true,
				fontSize: '14px',
				fontFamily: `'Roboto', sans-serif`,
				position: 'bottom',
				offsetX: 20,
				labels: {
					useSeriesColors: false,
				},
				markers: {
					width: 16,
					height: 16,
					radius: 5,
				},
				itemMargin: {
					horizontal: 15,
					vertical: 8,
				},
			},
			fill: {
				type: 'solid',
			},
			grid: {
				show: true,
			},
			dataLabels: {
				enabled: true,
				formatter: function (val) {
					return val.toString();
				},
				offsetY: 0,
				style: {
					fontSize: '12px',
				},
			},
		},

		series: [
			{
				name: 'Total users',
				data: yaxis,
			},
		],
	};
};

const getRegionLessonsChartProps = (
	stats: IEndUsersCountStatsByRegionLesson[],
	barBackground: string,
	onSelectLesson: (lesson: IEndUsersCountStatsByRegionLesson['lesson']) => void,
): IChartProps => {
	const xaxis: ApexXAxis = {
		type: 'category',
		categories: stats.map(stat => stat.lesson.name),
		title: {
			text: 'Number of users',
			style: {
				fontSize: '18px',
			},
		},
		tickAmount: 1,
		labels: {
			formatter: value => {
				return Number.parseInt(value).toString();
			},
		},
	};
	const yaxis: number[] = stats.map(stat => stat.totalCount);

	return {
		height: stats.length < 15 ? 500 : 70 * stats.length,
		type: 'bar',
		options: {
			colors: [barBackground],
			title: {
				text: ``,
				align: 'center',
			},
			chart: {
				id: 'bar-chart',
				stacked: true,
				toolbar: {
					show: true,
				},
				zoom: {
					enabled: true,
				},
				animations: {
					enabled: true,
					easing: 'easeinout',
					speed: 800,
					animateGradually: {
						enabled: true,
						delay: 150,
					},
					dynamicAnimation: {
						enabled: true,
						speed: 350,
					},
				},

				events: {
					dataPointSelection: function (_, __, config) {
						const lesson = stats[config.dataPointIndex]?.lesson;
						if (lesson) onSelectLesson(lesson);
					},
				},
			},
			responsive: [
				{
					breakpoint: 480,
					options: {
						legend: {
							position: 'bottom',
							offsetX: -10,
							offsetY: 0,
						},
					},
				},
			],
			plotOptions: {
				bar: {
					borderRadius: 4,
					horizontal: true,
				},
			},
			xaxis: xaxis,
			yaxis: {
				title: {
					text: 'Region lessons',
					style: {
						fontSize: '18px',
					},
				},
			},
			legend: {
				show: true,
				fontSize: '14px',
				fontFamily: `'Roboto', sans-serif`,
				position: 'bottom',
				offsetX: 20,
				labels: {
					useSeriesColors: false,
				},
				markers: {
					width: 16,
					height: 16,
					radius: 5,
				},
				itemMargin: {
					horizontal: 15,
					vertical: 8,
				},
			},
			fill: {
				type: 'solid',
			},
			grid: {
				show: true,
			},
			dataLabels: {
				enabled: true,
				formatter: function (val) {
					return val.toString();
				},
				offsetY: 0,
				style: {
					fontSize: '12px',
				},
			},
		},

		series: [
			{
				name: 'Total users',
				data: yaxis,
			},
		],
	};
};
